/**
 * Browser client entry — bundled by esbuild into `dist/client/app.js` and
 * loaded at the end of the SSR document (`renderAppDocument({ clientScriptUrl })`).
 *
 * It mounts the interactive {@link ClientApp} into the server-rendered root
 * container. The server HTML gives a fast, styled first paint and a JS-free
 * fallback; once this bundle runs, the app becomes fully interactive (forms
 * submit to the API, client-side navigation, validation, success/error
 * messages) and the service worker registers for install eligibility.
 *
 * We use `createRoot(...).render(...)` (not `hydrateRoot`) because the client
 * tree wires additional interactivity (client-side nav handlers) onto the same
 * layout; rendering fresh into the container avoids hydration-mismatch noise
 * while keeping the identical visible layout.
 */
import { createRoot } from "react-dom/client";
import { APP_ROOT_ID } from "../server/renderAppShell.js";
import { registerServiceWorker } from "../pwa/registerServiceWorker.js";
import { ClientApp } from "./ClientApp.js";
import { ConsoleShell } from "../console/ConsoleShell.js";
import { PlatformAdminApp } from "./PlatformAdminApp.js";

function start(): void {
  const container = document.getElementById(APP_ROOT_ID);
  if (container === null) {
    return;
  }
  const root = createRoot(container);
  const path = window.location.pathname;
  // The authenticated operator app lives under /app; the public auth ClientApp
  // (login / signup / verify-email / mfa) owns every other route.
  if (path === "/platform-admin" || path.startsWith("/platform-admin/")) {
    root.render(<PlatformAdminApp initialPath={path} />);
  } else if (path === "/app" || path.startsWith("/app/")) {
    root.render(<ConsoleShell initialPath={path} />);
  } else {
    root.render(<ClientApp initialPath={path} />);
  }
  // Register the SW so supported browsers report install eligibility (Req 21.2).
  void registerServiceWorker();
}

if (typeof document !== "undefined" && typeof window !== "undefined") {
  start();
}
